import { useMemo } from 'react';
import { useQuery } from 'urql';

import {
  ROLE_V2_SELECTION_LIST_QUERY,
  TaxonomyType,
} from '@revelio/data-access';
import type { SelectionList } from '@revelio/iso-utility';

import { useTaxonomyTree } from '../../../shared/selection-lists/hooks/useTaxonomyTree';
import { treeToRoleSelectionLists } from '../../../shared/selection-lists/utils/treeToRoleSelectionLists';
import { isV3Taxonomy } from '../utils/job-taxonomy-version-migration';

// The role taxonomy tree exposes levels 1-7. DataBuilder names the top level
// `job_category`; the tree's end-tag aliasing lives in treeToRoleSelectionLists.
const ROLE_TREE_LEVELS = [1, 2, 3, 4, 5, 6, 7];
const ROLE_TOP_LEVEL_KEY = 'job_category';

/**
 * Fetches role selection lists for Data Builder. V3 resolves from the taxonomy
 * tree (TaxonomyType.Role) honouring the selected custom job taxonomy id; V2
 * keeps using the legacy ROLE_V2_SELECTION_LIST_QUERY.
 */
export const useRoleSelectionLists = (
  requiredRoleLists: string[] = [],
  options: { jobTaxonomyVersion?: string; taxonomyId?: number }
) => {
  const isV3 = isV3Taxonomy(options.jobTaxonomyVersion);
  const taxonomyId = options.taxonomyId;

  const { treeData, levels, fetching } = useTaxonomyTree({
    taxonomyType: TaxonomyType.Role,
    taxonomyId,
    levels: ROLE_TREE_LEVELS,
    pause: !isV3,
  });

  const [{ data: legacyData }] = useQuery({
    query: ROLE_V2_SELECTION_LIST_QUERY,
    pause: isV3 || requiredRoleLists.length === 0,
  });

  const transformedRoleLists = useMemo(() => {
    const result: Record<string, SelectionList> = {};

    if (isV3) {
      if (treeData.length === 0) return result;

      // Tree → role selection lists, re-keyed to DataBuilder's vocabulary
      // (job_category at the top) on both list ids and ancestor links so the
      // picker can still walk job_category → role_k50 → …
      const byKey = treeToRoleSelectionLists(treeData, levels, {
        topLevelKey: ROLE_TOP_LEVEL_KEY,
      });

      requiredRoleLists.forEach((listId) => {
        const items = byKey[listId];
        if (items) {
          result[listId] = {
            id: listId,
            label: listId,
            value: items as unknown as SelectionList['value'],
          } as SelectionList;
        }
      });
    } else if (legacyData?.selectionList) {
      requiredRoleLists.forEach((listId) => {
        const roleList =
          legacyData.selectionList?.[
            listId as keyof NonNullable<typeof legacyData.selectionList>
          ];

        if (roleList && Array.isArray(roleList)) {
          result[listId] = {
            id: listId,
            label: listId,
            value: roleList,
          } as SelectionList;
        }
      });
    }

    return result;
  }, [isV3, treeData, levels, legacyData, requiredRoleLists]);

  return {
    roleSelectionLists: transformedRoleLists,
    isV3Taxonomy: isV3,
    taxonomyId: taxonomyId ?? undefined,
    isLoading: fetching,
  };
};
